import type { SortOrder, SortableProps } from '../types/sorting';
import type { SearchResult } from '../types/search';
import type { ResourceLibraryItem, ResourceLibraryItemBase } from '../types/resourceLibraryItems';

const DEFAULT_DATE_SORT_PROPS: SortableProps[] = ['lastModificationDate', 'publicationDate'];

/**
 * Returns the timestamp (in milliseconds) from the first valid date property
 * in the given order. If none is found, returns 0 (the Unix epoch).
 */
function getValidTimestamp(item: ResourceLibraryItemBase, sortProps: SortableProps[]): number {
    for (const prop of sortProps) {
        const value = item[prop];
        if (value) {
            const ts = new Date(value).getTime();
            if (!isNaN(ts)) {
                return ts;
            }
        }
    }
    return 0;
}

/**
 * Sorts the search results according to the given sort order.
 *
 * For 'latest' and 'oldest', it precomputes the timestamp from the provided date properties (by default
 * using lastModificationDate then publicationDate) to avoid redundant date parsing.
 *
 * For 'relevance', it sorts by the relevance score (higher score first).
 *
 * @param results - Array of search results to sort.
 * @param sortOrder - The sort order ('latest', 'oldest', or 'relevance').
 * @param sortProps - Optional array of date properties to use for sorting. Defaults to DEFAULT_DATE_SORT_PROPS.
 * @returns The sorted array of search results.
 */
export function sortItems<T extends ResourceLibraryItem>(
    results: SearchResult<T>[],
    sortOrder: SortOrder,
    sortProps: SortableProps[] = DEFAULT_DATE_SORT_PROPS
): SearchResult<T>[] {
    if (sortOrder === 'latest' || sortOrder === 'oldest') {
        const factor = sortOrder === 'latest' ? -1 : 1;

        // Precompute the timestamp for each result (Schwartzian transform)
        const decorated = results.map((result) => ({
            result,
            timestamp: getValidTimestamp(result.item, sortProps),
        }));

        decorated.sort((a, b) => factor * (a.timestamp - b.timestamp));
        return decorated.map((decoratedItem) => decoratedItem.result);
    } else if (sortOrder === 'relevance') {
        // Sort by relevance score, descending (higher score first)
        return results.sort((a, b) => b.relevanceScore - a.relevanceScore);
    }

    return results;
}
